import React, { useState } from 'react';
import PLUploadPanel from './PLUploadPanel';
import { CURRENCIES, MN, NM } from '../utils/constants';
import { getDealTerms } from '../utils/stateFactory';
import { getPairLabel, OTHER_CURRENCIES } from '../hooks/useFXRates';
import { ml } from '../utils/format';

const YEARS = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 1 + i);
// Formatted number input for standing data
function FormattedInput({ value, onChange, disabled }) {
  const [focused, setFocused] = React.useState(false);
  const display = focused
    ? (value || '')
    : (parseFloat(value) > 0 ? Math.round(parseFloat(value)).toLocaleString('en-US') : (value || ''));
  return (
    <input
      type={focused ? 'number' : 'text'}
      style={{ width: 110, textAlign: 'right', background: 'var(--s2)', border: '1px solid var(--e1)',
        borderRadius: 'var(--r)', padding: '5px 8px', fontSize: 12, fontFamily: 'var(--sans)',
        color: 'var(--t1)', outline: 'none' }}
      value={display}
      disabled={disabled}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={e => onChange(e.target.value)}
    />
  );
}


const FUNC_CURRENCIES = ['USD', 'GBP', 'EUR'];

function AddSubRow({ onAdd }) {
  const [val, setVal] = React.useState('');
  return (
    <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
      <input
        style={{ flex: 1, maxWidth: 180, background: 'var(--s2)', border: '1px solid var(--e1)',
          borderRadius: 'var(--r)', padding: '3px 7px', fontSize: 11, color: 'var(--t1)', outline: 'none', fontFamily: 'var(--sans)' }}
        placeholder="Sub-category name…"
        value={val}
        onChange={e => setVal(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && val.trim()) { onAdd(val.trim()); setVal(''); } }}
      />
      <button style={{ fontSize: 9, padding: '3px 10px', background: 'transparent', border: '1px dashed var(--e1)', color: 'var(--t3)', borderRadius: 'var(--r)', cursor: 'pointer' }}
        onClick={() => { if (val.trim()) { onAdd(val.trim()); setVal(''); } }}>+ Sub</button>
    </div>
  );
}

export default function StandingDataPanel({
  games, DT, updateGames, updateDT, readOnly = false,
  startDate, onUpdateStartDate,
  functionalCurrency, onUpdateFunctionalCurrency,
  fxRates, onUpdateFXRate,
  currentMonthIdx,
  departments, onUpdateDepartments,
  categories, onUpdateCategories,
  plUpload,
  ohValues,
  onRestoreOhValues,
}) {
  const [standingTab, setStandingTab] = useState('other');
  const [dealGame, setDealGame] = useState(games[0] || '');

  const deleteGame = (idx) => {
    if (!window.confirm(`Delete "${games[idx]}"? This will remove all forecast data for this game.`)) return;
    const newGames = games.filter((_, i) => i !== idx);
    updateGames(newGames, undefined, undefined, undefined);
    if (dealGame === games[idx]) setDealGame('');
  };

  const addGame = () => updateGames([...games, 'New Game'], undefined, undefined, undefined);

  const dt     = dealGame ? getDealTerms(DT, dealGame) : null;
  const sdMonth = startDate?.month ?? '';
  const sdYear  = startDate?.year  ?? '';

  return (
    <div className="panel">
      {/* Standing Data sub-tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--e0)', marginBottom: 18 }}>
        {[
          { id: 'upload', label: 'Data Upload' },
          { id: 'game',   label: 'Game Data' },
          { id: 'other',  label: 'Other Data' },
        ].map(t => (
          <button key={t.id} onClick={() => setStandingTab(t.id)}
            style={{ padding: '8px 18px', background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 11, letterSpacing: '.06em', fontFamily: 'var(--sans)',
              color: standingTab === t.id ? 'var(--blue)' : 'var(--t3)',
              borderBottom: standingTab === t.id ? '2px solid var(--blue)' : '2px solid transparent',
              marginBottom: -1, transition: 'all .15s' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* DATA UPLOAD TAB */}
      {standingTab === 'upload' && (
        <PLUploadPanel
          uploads={plUpload?.uploads || []}
          accounts={plUpload?.accounts || []}
          mappings={plUpload?.mappings || {}}
          snapshots={plUpload?.snapshots || []}
          loading={plUpload?.loading || false}
          uploadPL={plUpload?.uploadPL}
          saveMapping={plUpload?.saveMapping}
          restoreSnapshot={plUpload?.restoreSnapshot}
          ohCategories={categories}
          ohValues={ohValues}
          onRestoreOhValues={onRestoreOhValues}
        />
      )}

      {standingTab === 'game' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 14, marginBottom: 14 }}>
            {/* Game Library */}
            <div className="card">
              <div className="card-head"><span className="card-title">Game Library</span></div>
              <div className="card-body">
                <div className="game-list">
                  {games.map((g, idx) => (
                    <div key={idx} className="gi-item">
                      <span className="gi-n">{String(idx + 1).padStart(2, '0')}</span>
                      <input
                        value={g}
                        placeholder="Game name…"
                        disabled={readOnly}
                        onChange={e => {
                          if (readOnly) return;
                          const next = [...games];
                          next[idx] = e.target.value;
                          updateGames?.(next, idx, e.target.value);
                        }}
                      />
                      {!readOnly && (
                        <button className="rm" onClick={() => {
                          if (!window.confirm(`Delete "${games[idx]}"? This will remove all forecast data for this game.`)) return;
                          const next = games.filter((_, i) => i !== idx);
                          updateGames?.(next, idx, null);
                        }}>×</button>
                      )}
                    </div>
                  ))}
                </div>
                {!readOnly && (
                  <button className="add-g" onClick={() => updateGames?.([...games, 'New Game'], games.length, 'New Game')}>
                    + Add Game
                  </button>
                )}
              </div>
            </div>

            {/* Deal Terms */}
            <div className="card">
              <div className="card-head"><span className="card-title">Deal Terms</span></div>
              <div className="card-body">
                <div className="d-sel">
                  <label>Game</label>
                  <select value={dealGame} onChange={e => setDealGame(e.target.value)}>
                    <option value="">— select —</option>
                    {games.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </div>
                {dealGame ? (
                  <div>
                    {(() => {
                      const dt = DT[dealGame] || {};
                      const co = ['USD','GBP','EUR','JPY','AUD','CAD'].map(c =>
                        `<option value="${c}" ${dt.devAdvanceCurrency === c ? 'selected' : ''}>${c}</option>`
                      ).join('');
                      return (
                        <div>
                          <div className="d-sec">
                            <div className="d-sec-lbl">Contracted Development Advances</div>
                            <div className="d-row">
                              <label>Currency</label>
                              <select disabled={readOnly} value={dt.devAdvanceCurrency || 'USD'}
                                onChange={e => updateDT?.(dealGame, 'devAdvanceCurrency', e.target.value)}>
                                {['USD','GBP','EUR','JPY','AUD','CAD'].map(c => <option key={c} value={c}>{c}</option>)}
                              </select>
                            </div>
                            <div className="d-row">
                              <label>Amount</label>
                              <input type="number" disabled={readOnly} value={dt.devAdvanceAmt || ''}
                                placeholder="0" onChange={e => updateDT?.(dealGame, 'devAdvanceAmt', e.target.value)} />
                            </div>
                          </div>
                          <div className="d-sec">
                            <div className="d-sec-lbl">Budgeted Costs</div>
                            <div className="d-row">
                              <label>Publishing Costs Budget</label>
                              <input type="number" disabled={readOnly} value={dt.pubCostsBudget || ''}
                                placeholder="0" onChange={e => updateDT?.(dealGame, 'pubCostsBudget', e.target.value)} />
                            </div>
                            <div className="d-row">
                              <label>Marketing Budget</label>
                              <input type="number" disabled={readOnly} value={dt.marketingBudget || ''}
                                placeholder="0" onChange={e => updateDT?.(dealGame, 'marketingBudget', e.target.value)} />
                            </div>
                          </div>
                          <div className="d-sec">
                            <div className="d-sec-lbl">Royalties to Developer</div>
                            <div className="d-row">
                              <label>Pre-Recoup Rate</label>
                              <input type="number" step="0.1" disabled={readOnly} value={dt.royaltyPreRecoup || ''}
                                placeholder="0.0" onChange={e => updateDT?.(dealGame, 'royaltyPreRecoup', e.target.value)} />
                              <span className="pct-s">%</span>
                            </div>
                            <div className="d-row">
                              <label>Post-Recoup Rate</label>
                              <input type="number" step="0.1" disabled={readOnly} value={dt.royaltyPostRecoup || ''}
                                placeholder="0.0" onChange={e => updateDT?.(dealGame, 'royaltyPostRecoup', e.target.value)} />
                              <span className="pct-s">%</span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <p className="nomsg">Select a game to edit deal terms.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* OTHER DATA TAB */}
      {standingTab === 'other' && <div>


      {/* ── ROW 2: Forecast Settings ── */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-head"><span className="card-title">Forecast Settings</span></div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, auto) 1fr', gap: '0 32px', alignItems: 'end' }}>
            <div className="d-sec" style={{ marginBottom: 0 }}>
              <div className="d-sec-lbl">Forecast Start</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <select value={sdMonth} disabled={readOnly}
                  onChange={e => onUpdateStartDate?.({ month: parseInt(e.target.value), year: sdYear || new Date().getFullYear() })}>
                  <option value="">Month</option>
                  {MN.map((m, i) => <option key={i} value={i}>{m}</option>)}
                </select>
                <select value={sdYear} disabled={readOnly}
                  onChange={e => onUpdateStartDate?.({ month: sdMonth !== '' ? parseInt(sdMonth) : 0, year: parseInt(e.target.value) })}>
                  <option value="">Year</option>
                  {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              {startDate && <p style={{ fontSize: 11, color: 'var(--green)', marginTop: 6 }}>✓ {MN[startDate.month]} {startDate.year}</p>}
            </div>

            <div className="d-sec" style={{ marginBottom: 0 }}>
              <div className="d-sec-lbl">Functional Currency</div>
              <div style={{ marginTop: 8 }}>
                <select value={functionalCurrency || 'USD'} disabled={readOnly}
                  onChange={e => onUpdateFunctionalCurrency?.(e.target.value)}>
                  {FUNC_CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <p style={{ fontSize: 10, color: 'var(--t3)', marginTop: 6 }}>Revenue calculated in USD then converted</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── DEPARTMENTS ── */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-head"><span className="card-title">Departments (for People)</span></div>
        <div className="card-body">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
            {(departments || []).map((dept, i) => (
              <div key={dept.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input value={dept.name} disabled={readOnly} placeholder="Department name"
                  onChange={e => {
                    const next = departments.map((d, j) => j === i ? { ...d, name: e.target.value } : d);
                    onUpdateDepartments?.(next);
                  }}
                  style={{ flex: 1, maxWidth: 200, background: 'var(--s2)', border: '1px solid var(--e1)',
                    borderRadius: 'var(--r)', padding: '5px 8px', fontSize: 12, color: 'var(--t1)', outline: 'none' }}
                />
                <select value={dept.type} disabled={readOnly}
                  onChange={e => {
                    const next = departments.map((d, j) => j === i ? { ...d, type: e.target.value } : d);
                    onUpdateDepartments?.(next);
                  }}
                  style={{ fontSize: 11, padding: '4px 20px 4px 8px' }}>
                  <option value="overhead">Overhead</option>
                  <option value="developer">Developer</option>
                </select>
                <button className="rm" disabled={readOnly}
                  onClick={() => onUpdateDepartments?.(departments.filter((_, j) => j !== i))}>×</button>
              </div>
            ))}
            {(!departments || departments.length === 0) && (
              <p style={{ fontSize: 11, color: 'var(--t3)' }}>No departments added yet.</p>
            )}
          </div>
          {!readOnly && (
            <button className="add-g" onClick={() => onUpdateDepartments?.([...(departments||[]), { id: Math.random().toString(36).slice(2) + Date.now().toString(36), name: '', type: 'overhead' }])}>
              + Add Department
            </button>
          )}
        </div>
      </div>

      {/* ── OVERHEAD CATEGORIES ── */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-head"><span className="card-title">Overhead Categories</span>
          <span className="card-meta">Define categories and sub-categories for overhead costs</span>
        </div>
        <div className="card-body">
          {(categories || []).map((cat, ci) => (
            <div key={cat.id} style={{ marginBottom: 12, background: 'var(--s1)', border: '1px solid var(--e0)', borderRadius: 'var(--r2)', overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--s2)', borderBottom: '1px solid var(--e0)' }}>
                <input
                  className="lbl-i"
                  value={cat.name}
                  disabled={readOnly}
                  onChange={e => {
                    const next = (categories || []).map((c, i) => i === ci ? { ...c, name: e.target.value } : c);
                    onUpdateCategories?.(next);
                  }}
                  style={{ flex: 1, fontSize: 11, fontWeight: 600, color: 'var(--t1)' }}
                />
                {!readOnly && (
                  <button className="rm" onClick={() => {
                    if (!window.confirm('Delete this category?')) return;
                    onUpdateCategories?.((categories || []).filter((_, i) => i !== ci));
                  }}>×</button>
                )}
              </div>
              <div style={{ padding: '8px 12px' }}>
                {(cat.subs || []).map((sub, si) => (
                  <div key={sub.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 9, color: 'var(--t4)' }}>↳</span>
                    <input
                      className="lbl-i"
                      value={sub.name}
                      disabled={readOnly}
                      onChange={e => {
                        const next = (categories || []).map((c, cii) => cii === ci
                          ? { ...c, subs: c.subs.map((s, sii) => sii === si ? { ...s, name: e.target.value } : s) }
                          : c);
                        onUpdateCategories?.(next);
                      }}
                      style={{ flex: 1, fontSize: 11, color: 'var(--t2)' }}
                    />
                    {!readOnly && (
                      <button className="rm" style={{ fontSize: 11 }} onClick={() => {
                        const next = (categories || []).map((c, cii) => cii === ci
                          ? { ...c, subs: c.subs.filter((_, sii) => sii !== si) } : c);
                        onUpdateCategories?.(next);
                      }}>×</button>
                    )}
                  </div>
                ))}
                {!readOnly && (
                  <AddSubRow onAdd={name => {
                    const next = (categories || []).map((c, cii) => cii === ci
                      ? { ...c, subs: [...(c.subs || []), { id: Math.random().toString(36).slice(2) + Date.now().toString(36), name }] } : c);
                    onUpdateCategories?.(next);
                  }} />
                )}
              </div>
            </div>
          ))}
          {(!categories || categories.length === 0) && (
            <p style={{ fontSize: 11, color: 'var(--t3)', marginBottom: 10 }}>No categories yet.</p>
          )}
          {!readOnly && (
            <button className="add-g" onClick={() => {
              const name = prompt('Category name:');
              if (name?.trim()) onUpdateCategories?.([...(categories || []), { id: Math.random().toString(36).slice(2) + Date.now().toString(36), name: name.trim(), subs: [] }]);
            }}>+ Add Category</button>
          )}
        </div>
      </div>

      {/* ── ROW 3: FX Rates ── */}
      <div className="card">
        <div className="card-head">
          <span className="card-title">FX Rates</span>
          <span className="card-meta">Budget rates — enter monthly GBP/EUR conversion rates</span>
        </div>
        <div className="card-body" style={{ padding: '0 0 12px' }}>
          <div className="tw">
            <table className="dt">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', minWidth: 140 }}>Pair</th>
                  {Array.from({ length: NM }, (_, i) => (
                    <th key={i}>{ml(i, startDate)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {OTHER_CURRENCIES.map(other => {
                  const fc   = functionalCurrency || 'USD';
                  const pair = `${fc}_${other}`;
                  const isSame = fc === other;
                  return (
                    <tr key={pair} className={isSame ? 'rc' : 'ri'}>
                      <td style={{ fontStyle: isSame ? 'italic' : 'normal' }}>
                        {getPairLabel(fc, other)}{isSame ? ' (auto)' : ''}
                      </td>
                      {Array.from({ length: NM }, (_, i) => (
                        <td key={i}>
                          {isSame
                            ? <span style={{ color: 'var(--t4)', fontSize: 11 }}>1.0000</span>
                            : <input
                                type="number" step="0.0001" className="ci"
                                value={fxRates?.[pair]?.[i] || ''}
                                disabled={readOnly}
                                onChange={e => onUpdateFXRate?.(pair, i, e.target.value)}
                              />
                          }
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

    </div>}
    </div>
  );
}
